import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Headset,
  MessageSquare,
} from 'lucide-react';
import { login, ApiException } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { loginSchema, type LoginInput } from '../lib/schemas';
import { notify } from '../lib/notify';

export default function Login() {
  const [showPwd, setShowPwd] = useState(false);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/admin';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginInput) => {
    try {
      const result = await login(values.email, values.password);
      setSession(result.access_token, result.user as Parameters<typeof setSession>[1]);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.status === 401) {
          notify.error('Correo o contraseña incorrectos.');
        } else if (err.code === 'rate_limit_exceeded') {
          notify.error('Demasiados intentos.', err.message);
        } else {
          notify.error(err.message);
        }
      } else {
        notify.error('No pudimos iniciar tu sesión.', 'Reintentá en unos segundos.');
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-white flex">
      <aside className="hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/40 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-indigo-500/30 blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/10 backdrop-blur grid place-items-center ring-1 ring-white/20">
              <Headset size={20} />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">Vipsoft Soporte</div>
              <div className="text-xs text-white/60">soporte.vipsoft.cloud</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <h1 className="text-4xl font-semibold leading-tight tracking-tight">
              Una sola plataforma para
              <span className="text-blue-300"> todos tus canales</span> de soporte.
            </h1>
            <p className="text-white/70 leading-relaxed">
              Centraliza tickets que llegan por API, correo, WhatsApp, Telegram y tu web.
              Atiende más rápido, cumple tus SLAs y mide cada interacción.
            </p>

            <ul className="space-y-3 pt-2">
              {[
                { icon: ShieldCheck, text: 'Autenticación segura con JWT + 2FA' },
                { icon: MessageSquare, text: 'Multicanal: API, correo, WhatsApp, Telegram, widget' },
                { icon: Headset, text: 'SLAs, dashboard y reportes en tiempo real' },
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-white/80">
                  <span className="h-8 w-8 rounded-lg bg-white/10 grid place-items-center ring-1 ring-white/15">
                    <f.icon size={16} />
                  </span>
                  <span className="text-sm">{f.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Vipsoft · Todos los derechos reservados
          </div>
        </div>
      </aside>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-900 grid place-items-center text-white">
              <Headset size={18} />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">Vipsoft Soporte</div>
              <div className="text-xs text-slate-500">soporte.vipsoft.cloud</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
              Bienvenido de vuelta
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Iniciá sesión para gestionar tus tickets.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="tu@empresa.com"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    {...register('email')}
                    className={`w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition ${
                      errors.email
                        ? 'border-rose-300 focus:ring-rose-500 focus:border-rose-500'
                        : 'border-slate-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-rose-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700">Contraseña</label>
                </div>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    aria-invalid={errors.password ? 'true' : 'false'}
                    {...register('password')}
                    className={`w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition ${
                      errors.password
                        ? 'border-rose-300 focus:ring-rose-500 focus:border-rose-500'
                        : 'border-slate-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 rounded"
                    aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-rose-600 mt-1">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Ingresando…
                  </>
                ) : (
                  'Iniciar sesión'
                )}
              </button>
            </form>
          </div>

          <div className="mt-6 text-center text-xs text-slate-400">
            Protegido por reCAPTCHA · Política de privacidad · Términos
          </div>
        </div>
      </main>
    </div>
  );
}
